import axios from 'axios';
import { API_BASE_URL } from '../config/api';

export interface KYCStatusResponse {
  is_verified: boolean;
  has_pending_submission: boolean;
  verification_level: string;
  submission_id?: number;
  submitted_at?: string;
  reviewed_at?: string;
  status?: 'pending' | 'approved' | 'rejected';
  rejection_reason?: string;
  target_account_type?: string;
}

export interface KYCDocument {
  id: number;
  submission_id: number;
  document_type: string;
  file_path: string;
  file_name: string;
  file_size: number;
  uploaded_at: string;
}

export interface KYCSubmission {
  id: number;
  user_id: number;
  status: 'pending' | 'approved' | 'rejected';
  submitted_at: string;
  reviewed_at?: string;
  rejection_reason?: string;
  documents: KYCDocument[];
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

const getAuthHeadersFormData = () => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`
    // Don't set Content-Type for FormData, browser will set it with boundary
  };
};

export const getKYCStatus = async (): Promise<KYCStatusResponse> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/kyc/status`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching KYC status:', error);
    // Return default status if API doesn't exist
    if (error.response?.status === 404) {
      return {
        is_verified: false,
        has_pending_submission: false,
        verification_level: 'none'
      };
    }
    throw error;
  }
};

export const submitKYC = async (documents: any): Promise<KYCSubmission> => {
  const formData = new FormData();
  
  // Handle both File[] and object with named documents
  if (Array.isArray(documents)) {
    // Old format: File[]
    documents.forEach((file: any) => {
      if (file) formData.append('files', file);
    });
  } else {
    // New format: { idCard, businessLicense, proofOfAddress, beneficiaryAccount, etc }
    Object.entries(documents).forEach(([key, value]: [string, any]) => {
      if (key === 'beneficiaryAccount' && value && typeof value === 'object') {
        // Handle beneficiary account object
        if (value.clientId) formData.append('sepay_client_id', value.clientId);
        if (value.apiKey) formData.append('sepay_api_key', value.apiKey);
        if (value.checksumKey) formData.append('sepay_checksum_key', value.checksumKey);
        // Legacy keys
        if (value.bankName) formData.append('bank_name', value.bankName);
        if (value.accountNumber) formData.append('account_number', value.accountNumber);
        if (value.accountHolder) formData.append('account_holder', value.accountHolder);
      } else if (key === 'accountType' && value && typeof value === 'string') {
        formData.append('target_account_type', value);
      } else if (value && value instanceof File) {
        // Handle file uploads
        formData.append('files', value);
      }
    });
  }
  
  const response = await axios.post(`${API_BASE_URL}/kyc/submit`, formData, {
    headers: getAuthHeadersFormData()
  });
  return response.data;
};

export const getKYCSubmission = async (submissionId: number): Promise<KYCSubmission> => {
  const response = await axios.get(`${API_BASE_URL}/kyc/submissions/${submissionId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getMyKYCSubmissions = async (): Promise<KYCSubmission[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/kyc/submissions`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching KYC submissions:', error);
    if (error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

// Export as default object for convenience
const kycApi = {
  getKYCStatus,
  submitKYC,
  getKYCSubmission,
  getMyKYCSubmissions
};

export default kycApi;

